import {
    Box,
    FormControl, MenuItem, Select, Slider,
    TextField,
    Typography,
    Switch,
    FormControlLabel,
} from "@mui/material";
import OptionSection from "./OptionSection";

const BulleSlide = {
    '& .MuiSlider-valueLabel': {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: '20px',
        height: '20px',
        width: '50px',
        fontSize: '12px'
    }
};

export const DEFAULT_COLOR_FIELDS = {
    dotColor: '#000000',
    dotType: 'square',
    dotGradient: false,
    dotGradientColor1: '#000000',
    dotGradientColor2: '#000000',
    backgroundColor: '#ffffff',
    backgroundRound: 0,
    backgroundGradient: false,
    backgroundGradientColor1: '#ffffff',
    backgroundGradientColor2: '#ffffff',
    cornerSquareType: 'square',
    cornerSquareColor: '#000000',
    cornerDotType: 'square',
    cornerDotColor: '#000000',
};

function FieldLabel({children}) {
    return (
        <Typography variant="caption" color="text.secondary" sx={{mb: 0.5, display: 'block'}}>
            {children}
        </Typography>
    );
}

// Flexbox qui s'enroule selon la largeur réelle du conteneur (pas la largeur de la fenêtre) :
// contrairement aux breakpoints sx (xs/sm/...), ça reste correct même dans un panneau étroit.
function FieldRow({children}) {
    return (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2}}>
            {children}
        </Box>
    );
}

function Field({children}) {
    return <Box sx={{flex: '1 1 140px'}}>{children}</Box>;
}

/**
 * Champs contrôlés couleurs/formes (points, arrière-plan, coins) d'un QR code.
 * Réutilisé par QrStyleForm (génération) et l'éditeur de palette.
 */
function QrColorFields({value, onChange}) {
    const fields = {...DEFAULT_COLOR_FIELDS, ...value};
    const set = (patch) => onChange({...patch});

    return (
        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2}}>
            <OptionSection title="Points">
                <FieldRow>
                    <Field>
                        <FieldLabel>Type</FieldLabel>
                        <FormControl fullWidth size="small">
                            <Select value={fields.dotType} onChange={(e) => set({dotType: e.target.value})}>
                                <MenuItem value="square">Carré</MenuItem>
                                <MenuItem value="rounded">Arrondi</MenuItem>
                            </Select>
                        </FormControl>
                    </Field>
                    {!fields.dotGradient ? (
                        <Field>
                            <FieldLabel>Couleur</FieldLabel>
                            <TextField type="color" size="small" value={fields.dotColor} onChange={(e) => set({dotColor: e.target.value})} fullWidth/>
                        </Field>
                    ) : (
                        <>
                            <Field>
                                <FieldLabel>Dégradé 1</FieldLabel>
                                <TextField type="color" size="small" value={fields.dotGradientColor1} onChange={(e) => set({dotGradientColor1: e.target.value})} fullWidth/>
                            </Field>
                            <Field>
                                <FieldLabel>Dégradé 2</FieldLabel>
                                <TextField type="color" size="small" value={fields.dotGradientColor2} onChange={(e) => set({dotGradientColor2: e.target.value})} fullWidth/>
                            </Field>
                        </>
                    )}
                </FieldRow>

                <FormControlLabel
                    sx={{m: 0}}
                    control={<Switch size="small" checked={fields.dotGradient} onChange={(e) => set({dotGradient: e.target.checked})}/>}
                    label={<Typography variant="body2">Dégradé de couleur</Typography>}
                />
            </OptionSection>

            <OptionSection title="Arrière-plan">
                <FieldRow>
                    {!fields.backgroundGradient ? (
                        <Field>
                            <FieldLabel>Couleur</FieldLabel>
                            <TextField type="color" size="small" value={fields.backgroundColor} onChange={(e) => set({backgroundColor: e.target.value})} fullWidth/>
                        </Field>
                    ) : (
                        <>
                            <Field>
                                <FieldLabel>Dégradé 1</FieldLabel>
                                <TextField type="color" size="small" value={fields.backgroundGradientColor1} onChange={(e) => set({backgroundGradientColor1: e.target.value})} fullWidth/>
                            </Field>
                            <Field>
                                <FieldLabel>Dégradé 2</FieldLabel>
                                <TextField type="color" size="small" value={fields.backgroundGradientColor2} onChange={(e) => set({backgroundGradientColor2: e.target.value})} fullWidth/>
                            </Field>
                        </>
                    )}
                    <Field>
                        <FieldLabel>Arrondi</FieldLabel>
                        <Slider sx={BulleSlide} size="small" value={fields.backgroundRound} onChange={(e) => set({backgroundRound: e.target.value})} min={0} max={1} step={0.1} valueLabelDisplay="auto"/>
                    </Field>
                </FieldRow>

                <FormControlLabel
                    sx={{m: 0}}
                    control={<Switch size="small" checked={fields.backgroundGradient} onChange={(e) => set({backgroundGradient: e.target.checked})}/>}
                    label={<Typography variant="body2">Dégradé d'arrière-plan</Typography>}
                />
            </OptionSection>

            <OptionSection title="Coins carrés">
                <FieldRow>
                    <Field>
                        <FieldLabel>Type</FieldLabel>
                        <FormControl fullWidth size="small">
                            <Select value={fields.cornerSquareType} onChange={(e) => set({cornerSquareType: e.target.value})}>
                                <MenuItem value="square">Carré</MenuItem>
                                <MenuItem value="extra-rounded">Très arrondi</MenuItem>
                                <MenuItem value="dot">Point</MenuItem>
                            </Select>
                        </FormControl>
                    </Field>
                    <Field>
                        <FieldLabel>Couleur</FieldLabel>
                        <TextField type="color" size="small" value={fields.cornerSquareColor} onChange={(e) => set({cornerSquareColor: e.target.value})} fullWidth/>
                    </Field>
                </FieldRow>
            </OptionSection>

            <OptionSection title="Points des coins">
                <FieldRow>
                    <Field>
                        <FieldLabel>Type</FieldLabel>
                        <FormControl fullWidth size="small">
                            <Select value={fields.cornerDotType} onChange={(e) => set({cornerDotType: e.target.value})}>
                                <MenuItem value="square">Carré</MenuItem>
                                <MenuItem value="dot">Point</MenuItem>
                            </Select>
                        </FormControl>
                    </Field>
                    <Field>
                        <FieldLabel>Couleur</FieldLabel>
                        <TextField type="color" size="small" value={fields.cornerDotColor} onChange={(e) => set({cornerDotColor: e.target.value})} fullWidth/>
                    </Field>
                </FieldRow>
            </OptionSection>
        </Box>
    );
}

export default QrColorFields;
